import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useEffect, useState } from "react";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import BarSpinner from "../../Components/Loader/BarSpinner.tsx";
import { Product } from "../../models/product.ts";
import {
  deleteProduct,
  getProducts,
  editProduct,
} from "../../Services/product.service.ts";
import styles from "./ProductTable.module.css";

const ProductTable = ({ onAddProduct, onEditProduct }) => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null,
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const fetchProducts = async () => {
    setLoading(true);
    const response = await getProducts();
    setProducts(Array.isArray(response.data) ? response.data : []);
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDelete = async (productId) => {
    setDeletingProductId(productId);
    setShowDeletePopup(true);
  };

  const confirmDelete = async () => {
    if (!deletingProductId) return;
    const result = await deleteProduct(deletingProductId);
    if (result.success) {
      setSnackbar({
        open: true,
        message: "Product deleted successfully.",
        severity: "success",
      });
      fetchProducts();
    } else {
      setSnackbar({
        open: true,
        message: "Failed to delete product.",
        severity: "error",
      });
    }
    setShowDeletePopup(false);
    setDeletingProductId(null);
  };

  const resetDeletePopup = () => {
    setShowDeletePopup(false);
    setDeletingProductId(null);
  };

  const handleToggleOutOfStock = async (product: Product) => {
    const result = await editProduct(product.id!, {
      ...product,
      isOutOfStock: !product.isOutOfStock,
    });
    if (result.success) {
      setSnackbar({
        open: true,
        message: `Product ${
          product.isOutOfStock ? "marked as in stock" : "marked as sold out"
        }.`,
        severity: "success",
      });
      fetchProducts();
    } else {
      setSnackbar({
        open: true,
        message: "Failed to update product status.",
        severity: "error",
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <h3 className={styles.title}>Manage your products</h3>
        <div className={styles.headerActions}>
          <button className={styles.addBtn} onClick={onAddProduct}>
            + Add Product
          </button>
        </div>
      </div>

      <hr className={styles.divider} />

      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loader}>
            <BarSpinner />{" "}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Category</th>
                {/* <th>Season</th> */}
                <th>Type</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className={styles.productCell}>
                      {p.images?.front && (
                        <img
                          src={p.images.front}
                          alt={p.name}
                          className={styles.productImg}
                        />
                      )}
                      <span>{p.name}</span>
                    </div>
                  </td>
                  <td>{p.category?.name || "-"} </td>
                  {/* <td>{p.season || "-"}</td> */}
                  <td>{p.type || "-"}</td>
                  <td>{p.price}</td>
                  <td>{p.stock || "-"}</td>
                  <td>
                    <button
                      className={styles.statusBtn}
                      style={{
                        backgroundColor: p.isOutOfStock ? "#f44336" : "#4caf50",
                        color: "white",
                        border: "none",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "500",
                      }}
                    >
                      {p.isOutOfStock ? "Sold Out" : "In Stock"}
                    </button>
                  </td>
                  <td>
                    <span
                      className={styles.actionIcon}
                      onClick={() => onEditProduct(p)}
                    >
                      <FiEdit2 />
                    </span>
                    <span
                      className={styles.actionIcon}
                      onClick={() => handleDelete(p.id)}
                    >
                      <FiTrash2 />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDeletePopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h4>Delete Product</h4>
            <p className={styles.deleteMessage}>
              Are you sure you want to delete this product? This action cannot
              be undone.
            </p>
            <div className={styles.popupActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={resetDeletePopup}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.deleteBtn}
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default ProductTable;
